import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { resolveTenantId } from '../utils/tenantUtils'
import { getConfigurationServiceBaseUrl } from '../lib/apiBaseUrl'
import { getTenantContent } from '../services/tenantService'

// Bank Types
export interface BankIcon {
  _id: string
  name: string
  abbreviation: string
  bankCode: string
  imageUrl: string
  isActive?: boolean
  createdAt: string
  updatedAt: string
  __v: number
}

export interface BankIconsResponse {
  success: boolean
  data?: BankIcon[]
  error?: string
}

interface BankContextType {
  bankIcons: BankIcon[]
  isLoadingBankIcons: boolean
  error: string | null
  getBankIcon: (bankCode: string) => BankIcon | undefined
  getAvailableBanks: () => BankIcon[]
  refreshBankIcons: () => Promise<void>
}

const BankContext = createContext<BankContextType | undefined>(undefined)

const normalizeBankLookupValue = (value?: string): string => {
  return (value || '').trim().toLowerCase()
}

interface BankProviderProps {
  children: ReactNode
  tenantId?: string
}

export const BankProvider: React.FC<BankProviderProps> = ({ children, tenantId: runtimeTenantId }) => {
  const { user } = useAuth()
  const [bankIcons, setBankIcons] = useState<BankIcon[]>([])
  const [isLoadingBankIcons, setIsLoadingBankIcons] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchBankIcons = async (forceFresh = false) => {
    try {
      setIsLoadingBankIcons(true)
      setError(null)

      const baseURL = getConfigurationServiceBaseUrl()
      const globalBanksUrl = `${baseURL}/public/global-banks`

      // Prefer the tenant already resolved by App.tsx so providers stay consistent.
      const resolvedTenantId = runtimeTenantId?.trim() || await resolveTenantId(user || undefined)
      console.log("🏢 Using tenant ID:", resolvedTenantId);

      // First try to get tenant-specific banks
      let response
      if (resolvedTenantId) {
        console.log("🔍 Attempting to fetch tenant-specific banks for:", resolvedTenantId);
        try {
          const tenantData = await getTenantContent(resolvedTenantId, { forceFresh })
          console.log("Full tenantData response:", tenantData);
          if (tenantData.success && tenantData.data?.bankList && tenantData.data.bankList.length > 0) {
            console.log("Using tenant-specific banks:", tenantData.data?.bankList);
            setBankIcons(tenantData.data.bankList as unknown as BankIcon[])
            return
          } else {
            console.log("No tenant banks found, will fallback to global banks");
          }
        } catch (err) {
          console.error('❌ Failed to fetch tenant-specific banks:', err);
          console.warn('🔄 Falling back to global banks due to error')
        }
      } else {
        console.log("❌ No tenant ID found, falling back to global banks");
      }

      // Fallback to global banks (แหล่งเดียวกับ admin แท็บ "ธนาคารกลาง")
      response = await fetch(globalBanksUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: BankIconsResponse = await response.json()
      console.log("Global banks response:", data);

      if (data.success && data.data) {
        console.log("Using global banks:", data.data);
        setBankIcons(data.data)
      } else {
        throw new Error(data.error || 'Failed to fetch bank icons')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred'
      setError(errorMessage)
      console.error('Error fetching bank icons:', errorMessage)
    } finally {
      setIsLoadingBankIcons(false)
    }
  }

  const getBankIcon = (bankCode: string): BankIcon | undefined => {
    const lookup = normalizeBankLookupValue(bankCode)
    if (!lookup) return undefined

    return bankIcons.find((bank) => {
      const abbreviation = normalizeBankLookupValue(bank.abbreviation)
      const code = normalizeBankLookupValue(bank.bankCode)
      const name = normalizeBankLookupValue(bank.name)
      return lookup === abbreviation || lookup === code || lookup === name
    })
  }

  const getAvailableBanks = (): BankIcon[] => {
    return bankIcons.filter(bank => bank.isActive !== false)
  }

  const refreshBankIcons = async () => {
    await fetchBankIcons(true)
  }

  useEffect(() => {
    fetchBankIcons()
  }, [runtimeTenantId, user?.tenantId])

  const value: BankContextType = {
    bankIcons,
    isLoadingBankIcons,
    error,
    getBankIcon,
    getAvailableBanks,
    refreshBankIcons,
  }

  return <BankContext.Provider value={value}>{children}</BankContext.Provider>
}

export const useBankIcons = (): BankContextType => {
  const context = useContext(BankContext)
  if (context === undefined) {
    throw new Error('useBankIcons must be used within a BankProvider')
  }
  return context
}
